const LOCALE = "de-AT";

const decimalFormatters = new Map<string, Intl.NumberFormat>();

function getDecimalFormatter(min: number, max: number): Intl.NumberFormat {
  const key = `${min}-${max}`;
  let fmt = decimalFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(LOCALE, {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
      useGrouping: true,
    });
    decimalFormatters.set(key, fmt);
  }
  return fmt;
}

const currencyFormatter = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat(LOCALE, {
  maximumFractionDigits: 0,
  useGrouping: true,
});

function safe(n: number | null | undefined): number {
  return typeof n === "number" && Number.isFinite(n) ? n : 0;
}

/**
 * Parst eine im deutschen Format eingegebene Zahl in einen JS-`number`.
 * Akzeptiert Tausenderpunkte, Komma als Dezimaltrenner und auch Punkte
 * als Dezimaltrenner (zur Toleranz gegenüber Copy-Paste / Tastenfeldern).
 * Liefert `null` für leere oder ungültige Eingaben.
 */
export function parseGermanNumber(input: string | null | undefined): number | null {
  if (input == null) return null;
  const trimmed = String(input).trim();
  if (trimmed === "") return null;
  const hasComma = trimmed.includes(",");
  const hasDot = trimmed.includes(".");
  let normalized = trimmed.replace(/\s/g, "");
  if (hasComma && hasDot) {
    // 1.234,56 -> 1234.56
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    // 1234,56 -> 1234.56
    normalized = normalized.replace(",", ".");
  } else if (hasDot) {
    const parts = normalized.split(".");
    if (parts.length > 2) {
      // Mehrere Punkte: eindeutig Tausendertrenner (1.234.567)
      normalized = parts.join("");
    } else if (
      parts.length === 2 &&
      parts[1].length === 3 &&
      parts[0].length > 0 &&
      parts[0].length <= 3
    ) {
      // Genau ein Punkt mit drei Ziffern danach (z. B. 1.234) – im
      // deutschen Format als Tausendertrenner interpretieren.
      normalized = parts.join("");
    }
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/** Formatiert eine Zahl mit beliebigen Nachkommastellen im deutschen Format. */
export function formatDecimal(
  value: number | null | undefined,
  fractionDigits: number = 2,
  options?: { minFractionDigits?: number },
): string {
  const min = options?.minFractionDigits ?? fractionDigits;
  return getDecimalFormatter(min, fractionDigits).format(safe(value));
}

/** Formatiert eine Ganzzahl mit Tausendertrennzeichen. */
export function formatInteger(value: number | null | undefined): string {
  return integerFormatter.format(Math.round(safe(value)));
}

/** Formatiert einen Geldbetrag inklusive Euro-Symbol. */
export function formatCurrency(value: number | null | undefined): string {
  return currencyFormatter.format(safe(value));
}

/** Formatiert nur die Zahl eines Geldbetrags (ohne Symbol), z. B. für "X €". */
export function formatAmount(value: number | null | undefined): string {
  return formatDecimal(value, 2);
}

/** Formatiert eine Fläche mit zwei Nachkommastellen und Einheit m². */
export function formatArea(
  value: number | null | undefined,
  fractionDigits: number = 2,
): string {
  return `${formatDecimal(value, fractionDigits)} m²`;
}

/** Formatiert eine Länge in Metern. */
export function formatLength(
  value: number | null | undefined,
  fractionDigits: number = 2,
): string {
  return `${formatDecimal(value, fractionDigits)} m`;
}

/** Formatiert einen Prozentwert (Eingabe 0–100) ohne Nachkommastellen, mit Tausenderpunkt. */
export function formatPercent(
  value: number | null | undefined,
  fractionDigits: number = 0,
): string {
  return `${formatDecimal(value, fractionDigits)} %`;
}

/**
 * Slug→Label-Map für Tätigkeiten (`project.type`). Werte werden in der DB
 * als kleingeschriebene Slugs gespeichert; angezeigt werden sie aber stets
 * als sauber großgeschriebene Labels.
 */
const PROJECT_TYPE_LABELS: Record<string, string> = {
  betoninstandsetzung: "Betoninstandsetzung",
  oberflachenbehandlung: "Oberflächenbehandlung",
  rissabdichtung: "Rissabdichtung",
  korrosionsschutz: "Korrosionsschutz",
  strukturverstaerkung: "Strukturverstärkung",
  schutzschichten: "Schutzschichten auftragen",
  wasserschutz: "Wasserschutz",
  brandschutz: "Brandschutz",
};

/** Erster Buchstabe groß; Rest unverändert. Für Freitexte / Listenwerte. */
export function capitalizeFirst(value: string | null | undefined): string {
  if (!value) return "";
  const s = String(value);
  return s.charAt(0).toLocaleUpperCase("de-AT") + s.slice(1);
}

/**
 * Liefert das angezeigte Label für einen Tätigkeits-Slug. Unbekannte Werte
 * werden mit Großbuchstaben am Anfang dargestellt, damit auch Altdaten ohne
 * Map-Eintrag sauber aussehen.
 */
export function formatActivityLabel(value: string | null | undefined): string {
  if (!value) return "";
  const key = String(value).trim();
  if (!key) return "";
  return PROJECT_TYPE_LABELS[key.toLowerCase()] ?? capitalizeFirst(key);
}

/** Wendet `capitalizeFirst` auf jedes Listenelement an. */
export function capitalizeList(values: ReadonlyArray<string> | null | undefined): string[] {
  return (values ?? []).map((v) => capitalizeFirst(v));
}
